import { Box } from '@chakra-ui/react';
import { shouldRevalidateForAppUpdate } from '@ionq/app-update';
import { Loading } from '@ionq/legacy-ui';
import { createBrowserRouter } from 'react-router';
import AppLayout, { sessionLoader } from './components/AppLayout';
import RouteErrorFallback from './components/RouteErrorFallback';
import { shouldRevalidateForSessionSwitch } from './lib/sessionRevalidation';
import { replaceJobsBranch } from './lib/shellRoutes';
import NotFound from './pages/NotFound';

// The remote is a hard dependency, imported like any module: the shell has
// no content of its own, so if the remote can't load the app doesn't boot.
// Its routes are plain children of the data router — loaders, middleware
// and redirects in the remote tree run natively. The jobs branch is the
// exception: its list page is shell-owned (src/lib/shellRoutes.tsx).
const remoteRoutes = (await import('remote/routes')).default;

const { routes: shellRoutes, found: jobsBranchFound } =
    replaceJobsBranch(remoteRoutes);
if (!jobsBranchFound) {
    throw new Error(
        "remote/routes no longer exposes a 'jobs' branch — re-mount the shell-owned /jobs page (src/lib/shellRoutes.tsx)",
    );
}

// No /auth/callback route: the variant router pins the whole /auth prefix to
// the default app, and the identity stack resolves legacy params straight to
// final destinations — the return leg is never emitted anymore.
export const router = createBrowserRouter([
    {
        path: '/',
        element: <AppLayout />,
        // App-update guard, then users.me + the LD identify — resolved
        // before the chrome renders; signed-out/forbidden throw a document
        // redirect to the SSO mount.
        loader: sessionLoader,
        // Parents don't revalidate on child↔child navigations by default;
        // force the loader once an update is latched so the guard runs. No
        // UI-state exemption here (unlike enterprise): a latched param-only
        // navigation reloads onto the same URL, params preserved.
        // The session latch runs first and is consumed on read: an account
        // switch must re-run this loader exactly once (fresh users.me + LD
        // re-identify) even on a same-URL landing.
        shouldRevalidate: ({ defaultShouldRevalidate }) =>
            shouldRevalidateForSessionSwitch() ||
            shouldRevalidateForAppUpdate() ||
            defaultShouldRevalidate,
        // Chunk-failure recovery renders a spinner here while the guarded
        // reload lands; everything else gets the standard error screen.
        errorElement: <RouteErrorFallback />,
        // #root has no height, so without a viewport-height wrapper Loading's
        // height='full' collapses and pins the spinner to the top.
        hydrateFallbackElement: (
            <Box height='100dvh'>
                <Loading />
            </Box>
        ),
        children: shellRoutes,
    },
    {
        // Outside the chrome: unmatched URLs run neither the remote's auth
        // guard nor the root loader AppSidebar reads.
        path: '*',
        element: (
            <Box height='100dvh'>
                <NotFound />
            </Box>
        ),
    },
]);
